import { EMPTY, from, interval, Observable, of, ReplaySubject, Subject } from 'rxjs';
import { catchError, distinctUntilChanged, finalize, map, mergeMap, retry, share, startWith, switchMap, takeUntil, tap } from 'rxjs/operators';

import { authorizedFetch, readJson, postJson, fetchNdJson } from './http.js';
import { dynamicMergeMap } from './rx-util.js';

export let model = 'gemma4:e2b';
export let researchModel = 'gemma4:e2b';
export let maxContextWindow = 128000;

export let onlineNodes$ = interval(1*60*1000).pipe(
    startWith(undefined),
    switchMap(() => from(authorizedFetch('/api/online-nodes')).pipe(
        readJson(),
        catchError(err => {
            // handle online check errors here. we don't want to show
            // errors to the user 'just' because the online status was
            // interrupted for a while.
            console.log('Failed to fetch online status:', err);
            return of([]);
        }),
    )),
    map(nodes => {
        let count = 0;
        for (let nd of nodes) {
            for (let m of nd.tags.models) {
                if (m.model === model) {
                    ++count;
                }
            }
        }
        return count;
    }),
    share({
        connector: () => new ReplaySubject(1),
        resetOnComplete: true,
        resetOnRefCountZero: true,
        resetOnError: true,
    }),
    distinctUntilChanged(),
);

export function generate(prompt, opts={}) {
    return of(undefined).pipe(
        switchMap(() => queue(of(undefined).pipe(
            switchMap(() => postJson('/api/generate', {
                stream: false,
                ...opts,
                model,
                prompt,
            })),
        ))),
        llmRetry(),
    );
}

export function chat(messages, opts={}) {
    return of(undefined).pipe(
        switchMap(() => queue(of(undefined).pipe(
            switchMap(() => fetchNdJson('/api/chat', {
                method: 'POST',
                body: JSON.stringify({
                    ...opts,
                    model,
                    messages,
                }),
            })),
        ))),
        startWith({
            init: true,
            done: false,
        }),
        llmRetry(),
    );
}

export function answer(messages, opts={}) {
    return of(undefined).pipe(
        switchMap(() => queue(of(undefined).pipe(
            switchMap(() => new Observable(observer => {
                let ctlr = new AbortController();
                (async function() {
                    let res = await authorizedFetch('/api/chat', {
                        signal: ctlr.signal,
                        method: 'POST',
                        body: JSON.stringify({
                            ...opts,
                            stream: false,
                            model,
                            messages,
                        }),
                    });
                    if (!res.ok) {
                        observer.error(res);
                        return;
                    }
                    let body = await res.json();
                    observer.next(body);
                    observer.complete();
                }()).catch(err => observer.error(err));
                return () => ctlr.abort();
            })),
        ))),
        llmRetry(),
    );
}

function llmRetry() {
    return retry({
        count: 1,
        delay: 2000 + 1000*Math.random(),
    });
}

let queue$ = new Subject();

function queue(fetch$) {
    return new Observable(observer => {
        let destroy$ = new Subject();
        queue$.next({
            fetch$,
            observer,
            destroy$,
        });
        return () => {
            destroy$.next();
            destroy$.complete();
        };
    });
}

export let gptQueue$ = queue$.pipe(
    dynamicMergeMap(task => task.fetch$.pipe(
        tap(res => task.observer.next(res)),
        catchError(err => {
            task.observer.error(err);
            return EMPTY;
        }),
        finalize(() => task.observer.complete()),
        takeUntil(task.destroy$),
    ), onlineNodes$.pipe(
        map(onlineNodes => Math.max(1, onlineNodes)),
    )),
);
